import {
  Component,
  OnInit,
  ViewChild,
  Inject,
  OnDestroy,
  ChangeDetectorRef,
  TemplateRef,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatStepHeader, MatStepper } from '@angular/material/stepper';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '@services/alert.service';
import { ApiService } from '@services/api.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { DOCUMENT } from '@angular/common';
import { ParamsService } from '@services/params.service';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { TermsAndConditionsComponent } from '@shared/mat-dialogs/terms-and-conditions/terms-and-conditions.component';
import {
  Subscription,
  Subject,
  takeUntil,
  from,
  delay,
  tap,
  switchMap,
  of,
  catchError,
} from 'rxjs';
import { DevCycleService } from '@services/dev-cycle.service';
import { EntryKeys } from '@shared/upload-documents/upload-documents.component';
import {
  MatAccordionComponent,
  UploadedDocsStatus,
} from '@shared/mat-accordion/mat-accordion.component';
import { Tracking } from '@shared/tracking/tracking.interface';
import { TreoMessage } from '@treo/components/message';
import {
  BANK_TRANSFER_METHODS,
  CARD_PAYMENT_METHODS,
  MANDATORY_INVOICE_COUNTRIES,
  PAYMENT_METHOD,
} from '@constants/constants';
import { NgxMaskPipe } from 'ngx-mask';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { DevCycleUser } from '@devcycle/js-client-sdk';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { environment } from '@env/environment';
import { TreoMediaWatcherService } from '@treo/services/media-watcher';

import { Userpilot } from 'userpilot';
export enum UPLOAD_STATUS {
  Uploaded = 'Completed',
  Pending = 'Pending',
}

export type DocumentType = {
  label:
    | 'paymentRequest:proofOfPayment'
    | 'uploadDocuments:passportOrId'
    | 'uploadDocuments:invoice'
    | 'invoiceFunds:netInvoiceFile'
    | 'invoiceFunds:proofOfPayment'
    | 'commissions:netCommissionFile'
    | 'commissions:proofOfPayment';
  key: 'proofOfPayment' | 'nationalId' | 'invoice' | 'commission';
  status: UPLOAD_STATUS;
};

export enum DOCUMENTS_MASK {
  Br = '000.000.000-00',
}

const auth0Client = new Auth0Client({
  domain: environment.authDomain,
  clientId: environment.authClientId,
});

@Component({
  selector: 'ew-payment-request-v1',
  templateUrl: './payment-request.component.html',
  styleUrls: ['./payment-request.component.scss'],
})
export class PaymentRequestV1Component implements OnInit, OnDestroy {
  requestIdUpload: boolean = false;
  isMobile: boolean = false;
  welcome: boolean = true;

  constructor(
    private API: ApiService,
    private TRANSLATE: TranslateService,
    private AR: ActivatedRoute,
    private ALERT: AlertService,
    private DS: DomSanitizer,
    private PRMS: ParamsService,
    private MD: MatDialog,
    private HTTP: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private CYCLE: DevCycleService,
    private maskPipe: NgxMaskPipe,
    private cdr: ChangeDetectorRef,
    private mediaWatcher: TreoMediaWatcherService,
  ) {}

  ngOnDestroy(): void {
    this.documentSubscription?.unsubscribe();
    this.queryParamMapSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDocumentChange() {
    this.documentSubscription = this.payer.controls[
      'document'
    ].valueChanges.subscribe((value) => {
      if (value !== undefined && this.documentMask) {
        this.payer.controls['document'].setValue(
          this.maskPipe.transform(value, this.documentMask),
          { emitEvent: false },
        );
      }
    });
  }

  private authAttempts = 0;

  userPilotIdentify(value: any) {
    const data = value?.record;
    if (!data) return;

    const company = data?.company;
    const payer = data?.payer;

    // Use RxJS to handle authentication errors gracefully
    from(auth0Client.getTokenSilently())
      .pipe(
        // If token is obtained successfully, get the user
        switchMap(() => from(auth0Client.getUser())),
        // If any error occurs in the authentication process, catch it and return null
        catchError((error) => {
          this.authAttempts++;
          if (this.authAttempts >= 3) {
            return of(null); // Stop further attempts
          }
          return of(null);
        }),
        // Take until component is destroyed to prevent memory leaks
        takeUntil(this.destroy$),
      )
      .subscribe((user) => {
        // If no user is authenticated, identify as anonymous
        if (!user) {
          Userpilot.anonymous({
            company: {
              id: company?.id,
              name: company?.name,
            },
            payer: payer,
          });
        }
      });
  }

  async ngOnInit() {
    this.onDocumentChange();
    this.changeRequestIdFeatureFlag();
    this.paymentLinkNotWorkingFeatureFlag = await this.CYCLE.getVariable(
      'ew-2104-payment-link-not-working',
      false,
    );
    this.status = this.PRMS.params.get('tm_payment_status');
    this.AR.params.subscribe((params: any) => {
      this.API.get(`v1/public/payment-requests/${params.id}`).subscribe(
        async (value: any) => {
          const key = 'visitedPaymentRequests';
          const visitedRaw = localStorage.getItem(key);
          const visited = visitedRaw ? JSON.parse(visitedRaw) : {};

          if (!visited[params.id]) {
            this.welcome = true;
            visited[params.id] = true;
            localStorage.setItem(key, JSON.stringify(visited));
          } else {
            this.welcome = false;
          }

          const devCycleUser: DevCycleUser = {
            customData: {
              companyName: value?.record?.company?.name,
              agencyCompanyId: value?.record?.company?.externalId
                ? parseInt(value?.record?.company?.externalId, 10)
                : null,
              companyId: value?.record?.company?.id,
            },
          };

          if (['CANCELED', 'CANCELLED'].includes(value?.record?.status)) {
            this.notFound = true;
            return;
          }

          this.userPilotIdentify(value);
          await this.CYCLE.devcycleClient.identifyUser(devCycleUser);

          this.mediaWatcher
            .onMediaQueryChange$('(max-width: 599px)')
            .pipe(
              tap((state) => (this.isMobile = state.matches)),
              takeUntil(this.destroy$),
              delay(1000),
            )
            .subscribe(() => {
              if (this.stepper) {
                this.updateStepHeaders(this.stepper.selectedIndex);
              }
            });

          await this.handleLoadPaymentRequest(value);
          this.getFormValuesFromUrl();
          this.paymentMethodChanged(this.payment.value.paymentMethod);
        },
        (error) => {
          if (error.status == 404) {
            this.notFound = true;
            this.API.get(`v1/public/company-data/${params.id}`).subscribe(
              (value) => {
                this.companyName = value.record.name;
                this.companyLogo = value.record.logo;
                const record = {
                  company: {
                    id: params.id,
                    name: value.record.name,
                  },
                };
                this.userPilotIdentify({ record: record });
              },
            );
          }
        },
      );
    });
  }

  getFormValuesFromUrl(): void {
    this.queryParamMapSubscription = this.AR.queryParamMap.subscribe(
      (query: any) => {
        this.fillFormValuesWithParameters(query, this.payment);
        this.fillFormValuesWithParameters(query, this.payer);
      },
    );
  }

  fillFormValuesWithParameters(query: any, form: FormGroup) {
    Object.keys(form.controls).forEach((field) => {
      if (query.params[field]) {
        form.get(field)?.setValue(query.params[field]);
        form.get(field)?.updateValueAndValidity();

        if (!form.get(field)?.valid) {
          form.get(field)?.setValue(null);
          form.get(field)?.updateValueAndValidity();
        }
      }
    });
  }

  queryParamMapSubscription: Subscription | undefined;
  documentSubscription: Subscription | undefined;

  companyName: string = '';
  companyLogo: string = '';
  startDate = new Date(1992, 0, 1);
  payers = [{ value: 'OTHER', label: 'agencyPaymentRequest:agent' }];
  private destroy$ = new Subject<void>();
  documentLabel = '';
  documentExample = '';
  documentDefault = '';
  documentMask: string | null = null;
  documentRequired = true;
  documentPassportRequired = false;
  notFound: boolean = false;
  paymentRequest: any = null;
  paymentMethods: any[] = [];
  loading: boolean = false;
  states: any[] = [];
  selectedIndex: number = 0;
  expired: boolean = false;
  paymentAmount!: string;
  paymentCurrency!: string;
  @ViewChild('stepper')
  stepper!: MatStepper;
  @ViewChild('matAccordion')
  matAccordion!: MatAccordionComponent;
  @ViewChild('dialogTemplate')
  dialogTemplate!: TemplateRef<any>;
  showBankTransfer: boolean = false;
  showCreditCard: boolean = false;
  redirectUrl!: SafeResourceUrl;
  externalPaymentUrl: string = '';
  submitted: boolean = false;
  status!: string | null;
  successStatuses: string[] = [
    'AUTHORIZED',
    'CREDITED',
    'IN_TRANSIT',
    'PAID',
    'RECEIVED',
    'Successful',
    'Pending',
  ];
  wasPassportAlreadyUploaded = false;
  hideDetails = false;

  payment: FormGroup = new FormGroup({
    countryPayFrom: new FormControl(null, [Validators.required]),
    statePayFrom: new FormControl(null),
    paymentMethod: new FormControl(null, [Validators.required]),
  });

  student: FormGroup = new FormGroup({
    firstname: new FormControl('', [Validators.required]),
    lastname: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    address: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(255),
    ]),
    postalCode: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(10),
    ]),
    dateOfBirth: new FormControl('', [Validators.required]),
    email: new FormControl('', [
      Validators.required,
      Validators.email,
      Validators.maxLength(50),
    ]),
    phoneNumber: new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(20),
    ]),
  });

  payer: FormGroup = new FormGroup({
    payer: new FormControl('Agency', [Validators.required]),
    document: new FormControl(''),
    nationalId: new FormControl(''),
    whoIsMakingThePayment: new FormControl('', [Validators.required]),
    termsAccepted: new FormControl('', [
      Validators.required,
      Validators.requiredTrue,
    ]),
    firstname: new FormControl('', [Validators.required]),
    lastname: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    address: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(255),
    ]),
    postalCode: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(10),
    ]),
    email: new FormControl('', [
      Validators.required,
      Validators.email,
      Validators.maxLength(50),
    ]),
    phoneNumber: new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(20),
    ]),
  });

  documentIdToUpload: DocumentType[] = [
    {
      label: 'uploadDocuments:passportOrId',
      key: 'nationalId',
      status: UPLOAD_STATUS.Pending,
    },
  ];
  uploadedDocumentsIds: EntryKeys = {};

  confirmation: FormGroup = new FormGroup({});
  proofOfPaymentToUpload: DocumentType[] = [
    {
      label: 'paymentRequest:proofOfPayment',
      key: 'proofOfPayment',
      status: UPLOAD_STATUS.Pending,
    },
  ];
  hasUploadedDocs = UploadedDocsStatus.No;
  additionalDocuments = false;
  uploadedDocumentsId: EntryKeys = {};
  intentExpirationDate: string = '';
  tracking!: Tracking;
  message: TreoMessage | null = null;
  creditCardAlert: TreoMessage = {
    appearance: 'soft',
    type: 'warning',
    showButtonClose: false,
    showIcon: true,
    title: 'paymentRequest:creditCardAlertTitle',
    content: 'paymentRequest:creditCardAlertContent',
  };
  paymentLinkNotWorkingFeatureFlag = false;

  getTranslations(single: boolean, docs: UploadedDocsStatus) {
    const scenarios = {
      NO: (single: boolean) =>
        single
          ? [
              'paymentRequest:onlyProofNoUploadsTitle',
              'paymentRequest:onlyProofNoUploadsSubtitle',
              'paymentRequest:onlyProofNoUploadsButton',
            ]
          : [
              'paymentRequest:requiredDocsNoUploadsTitle',
              'paymentRequest:requiredDocsNoUploadsSubtitle',
              'paymentRequest:requiredDocsNoUploadsButton',
            ],
      PARTIAL: (single: boolean) =>
        !single
          ? [
              'paymentRequest:requiredDocsPartialUploadsTitle',
              'paymentRequest:requiredDocsPartialUploadsSubtitle',
              'paymentRequest:requiredDocsPartialUploadsButton',
            ]
          : [
              'paymentRequest:onlyProofUploadedTitle',
              'paymentRequest:onlyProofUploadedSubtitle',
              'paymentRequest:onlyProofUploadedButton',
            ],
      YES: (single: boolean) =>
        single
          ? [
              'paymentRequest:onlyProofUploadedTitle',
              'paymentRequest:onlyProofUploadedSubtitle',
              'paymentRequest:onlyProofUploadedButton',
            ]
          : [
              'paymentRequest:requiredDocsUploadedTitle',
              'paymentRequest:requiredDocsUploadedSubtitle',
              'paymentRequest:requiredDocsUploadedButton',
            ],
    };

    const [title, subtitle, cta] = scenarios[docs](single);

    return [title, subtitle, cta];
  }

  paymentMethodChanged(paymentMethod: Number) {
    this.documentPassportRequired = false;

    const payment_method = this.paymentMethods.find(
      (method) => method.paymentMethodId === paymentMethod,
    );

    if (payment_method) {
      this.paymentRequest.payment.convertedAmount =
        payment_method?.convertedAmount;
      this.paymentRequest.payment.convertedCurrency =
        payment_method?.convertedCurrency;
    }

    //Bank Transfer
    if (
      paymentMethod === PAYMENT_METHOD.BANK_TRANSFER ||
      paymentMethod === PAYMENT_METHOD.PIX_TRANSFER
    ) {
      if (
        MANDATORY_INVOICE_COUNTRIES.includes(this.payment.value.countryPayFrom)
      ) {
        this.proofOfPaymentToUpload = [
          {
            label: 'paymentRequest:proofOfPayment',
            key: 'proofOfPayment',
            status: UPLOAD_STATUS.Pending,
          },
          {
            label: 'uploadDocuments:invoice',
            key: 'invoice',
            status: UPLOAD_STATUS.Pending,
          },
        ];

        if (!this.requestIdUpload) {
          this.proofOfPaymentToUpload.push({
            label: 'uploadDocuments:passportOrId',
            key: 'nationalId',
            status: UPLOAD_STATUS.Pending,
          });
        }

        this.documentPassportRequired = true;
        this.additionalDocuments = true;
      }
    } else {
      this.proofOfPaymentToUpload = [
        {
          label: 'paymentRequest:proofOfPayment',
          key: 'proofOfPayment',
          status: UPLOAD_STATUS.Pending,
        },
      ];
    }

    Object.keys(this.uploadedDocumentsId).forEach((key) => {
      const index = this.proofOfPaymentToUpload.findIndex((a) => a.key === key);
      if (this.proofOfPaymentToUpload[index]) {
        this.proofOfPaymentToUpload[index].status = !this.uploadedDocumentsId[
          key as keyof EntryKeys
        ]
          ? UPLOAD_STATUS.Pending
          : UPLOAD_STATUS.Uploaded;
      }
    });

    this.setUploadDocumentAsRequired();
    this.updateHasUploadedDocs();
  }

  submit(stepper: MatStepper) {
    this.enableStudentFields();
    this.loading = true;
    this.message = null;
    this.student.value.dateOfBirth = moment(
      this.student.value.dateOfBirth,
    ).format('YYYY-MM-DD');

    if (!this.paymentRequest?.payment?.instructions || this.expired) {
      if (this.payment.value.countryPayFrom === 'cl') {
        this.payer.value.document = this.payer.value.document.replace('-', '');
      }

      this.API.post(
        `v1/public/payment-requests/${this.paymentRequest.uuid}/payment-intents/`,
        {
          payment: {
            ...this.payment.value,
            payment_method: this.payment.value.paymentMethod,
            // Determine where to redirect to when the payment completes
            statusRedirectUrl: window.location.href,
          },
          student: this.student.value,
          payer: this.payer.value,
        },
      ).subscribe({
        next: (data: any) => {
          const { payment, paymentIntent } = data;
          this.paymentRequest.payment = Object.assign(
            this.paymentRequest.payment,
            payment,
            { createdAt: paymentIntent?.created_at },
            { instructionsExpiry: paymentIntent?.instructions_expiry },
          );

          this.expired = false;
          this.submitted = true;
          this.status = null;

          setTimeout(() => {
            stepper.next();
          });

          if (data.payment.redirectUrl) {
            this.redirectUrl = this.DS.bypassSecurityTrustResourceUrl(
              data.payment.redirectUrl,
            );
            this.showCreditCard = true;
          }

          if (
            BANK_TRANSFER_METHODS.includes(this.payment.value.paymentMethod)
          ) {
            this.showBankTransfer = true;
            this.showCreditCard = false;
          }

          if (data.paymentIntent.invoice) {
            this.uploadedDocumentsId.invoice = data.paymentIntent.invoice;
            const invoiceDoc = this.proofOfPaymentToUpload.find(
              ({ key }) => key === 'invoice',
            );
            if (invoiceDoc) invoiceDoc.status = UPLOAD_STATUS.Uploaded;
          }
        },
        error: ({ error: error }) => {
          this.message = {
            appearance: 'outline',
            type: 'warn',
            showButtonClose: false,
            showIcon: true,
            customIcon: '',
            title: 'alert:errorTitle',
            content: this.buildContentMessage(error),
          };
          window.scrollTo(0, 0);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
    } else {
    }
  }

  private buildContentMessage(error: {
    message: string;
    success: string;
  }): string {
    return error.message;
  }

  getDocumentValidations(): void {
    this.TRANSLATE.get('createRequestMoney:payerInputDocument').subscribe(
      (value) => {
        this.documentDefault = value;
        this.documentLabel = this.documentDefault;
      },
    );

    this.API.get(
      `v1/public/country/${this.payment.value.countryPayFrom}`,
    ).subscribe({
      next: (res: any) => {
        const document = res.record.document;
        this.payer.get('document')?.clearValidators();
        if (res.success && document) {
          this.documentLabel = `${this.documentDefault}: ${document.name}`;
          this.documentExample = document.example;

          if (document.format) {
            this.payer
              .get('document')
              ?.addValidators(Validators.pattern(document.format));
          }

          if (document.requiredForPayer) {
            this.payer.get('document')?.addValidators([Validators.required]);
          }
          this.documentRequired = !!document.requiredForPayer;
          if (this.documentRequired && this.paymentRequest?.payer?.document) {
            this.payer
              .get('document')
              ?.setValue(this.paymentRequest.payer.document);
          } else {
            this.payer.get('document')?.setValue(undefined); // Keep consistent with original logic if not required
          }
        } else {
          this.documentRequired = false;
          this.documentLabel = this.documentDefault;
          this.documentExample = '';
          this.payer.get('document')?.setValue(undefined);
        }
        this.payer.get('document')?.updateValueAndValidity();
        this.setUploadDocumentAsRequired(); // Assuming this needs to be called
        this.cdr.detectChanges();
      },
      error: () => {
        this.documentRequired = false;
        this.documentLabel = this.documentDefault;
        this.documentExample = '';
        this.payer.get('document')?.clearValidators();
        this.payer.get('document')?.setValue(undefined);
        this.payer.get('document')?.updateValueAndValidity();
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * assign documentMask based on the @enum DOCUMENT_MASK.
   * @param countryPayFrom country that payer selected
   * @author Otávio Rodrigues.
   */
  setDocumentMask(countryPayFrom: string): void {
    //capitalize first character to follow enum pattern
    const countryMask: string = (DOCUMENTS_MASK as any)[
      countryPayFrom.replace(
        /\w\S*/g,
        (text) => text.charAt(0).toUpperCase() + text.substring(1),
      )
    ];

    if (countryMask) {
      this.documentMask = countryMask;
    } else {
      this.documentMask = '';
    }
  }

  /**
   * Handles changes to the payer's country (`countryPayFrom`).
   *
   * This function:
   * 1. Applies the appropriate document mask and validations based on the selected country.
   * 2. Updates the `student` and `payer` form groups with the selected country.
   * 3. Resets the available payment methods and fetches the list of states.
   * 4. Calls the API to retrieve available payment methods based on the selected country
   *    and the type of payment request (DEPOSIT or AGENCY).
   *
   * @returns {void} Does not return a value.
   * @author Martin Batun Tec.
   */
  onPayerCountryChange(): void {
    const country = this.payment.value.countryPayFrom;

    this.payer.get('document')?.reset();
    this.payer.get('document')?.clearValidators();
    this.documentLabel = this.documentDefault; // Assuming documentDefault is initialized
    this.documentExample = '';
    this.documentMask = null;
    this.documentRequired = false; // Default state

    if (!country) {
      this.paymentMethods = [];
      this.states = [];
      this.payment.get('statePayFrom')?.reset();
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    this.setDocumentMask(country);
    this.getDocumentValidations(); // This will now also call updateValueAndValidity and detectChanges

    this.student.patchValue({ country: country });
    this.payer.patchValue({ country: country });
    this.loading = true;
    this.getStates();
    this.paymentMethods = []; // Reset before fetching new ones

    const apiUrl =
      this.paymentRequest.type === 'DEPOSIT'
        ? `v1/public/payment-requests/${this.paymentRequest.uuid}/fx-conversion`
        : `v1/public/agency-payment-requests/${this.paymentRequest.uuid}/fx-conversion`;

    this.API.post(apiUrl, { countryPayFrom: country }).subscribe({
      next: (value: any) => {
        this.paymentMethods = value.paymentMethods;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.paymentMethods = []; // Ensure reset on error
        this.cdr.detectChanges();
      },
    });
  }

  openMatAccordion() {
    this.matAccordion.openPanel();
  }

  get showInputStates(): boolean {
    const country = this.payment.get('countryPayFrom')?.value;
    return ['us', 'ca', 'au'].includes(country);
  }

  getStates(): void {
    this.setStatePayFromValidators();
    if (this.showInputStates) {
      this.API.get(
        `v1/public/payment-requests/provinces/${this.payment.value.countryPayFrom}/`,
      ).subscribe((data: any) => {
        this.states = data.records;
      });
    } else {
      this.states = [];
      this.payment.get('statePayFrom')?.reset();
    }
  }

  setStatePayFromValidators(): void {
    const control = this.payment.get('statePayFrom');
    control?.setValidators(this.showInputStates ? [Validators.required] : null);
    control?.updateValueAndValidity();
  }

  moveToLastStep(paymentMethod: number) {
    if (paymentMethod === null) {
      return;
    }

    BANK_TRANSFER_METHODS.includes(paymentMethod as 19 | 0)
      ? (this.showBankTransfer = true)
      : (this.showCreditCard = true);

    this.stepper.linear = false;
    this.stepper.selectedIndex = 3;
    this.welcome = false;
    setTimeout(() => {
      this.stepper.linear = true;
    });
  }

  get instructionExpiry() {
    const initialDate = new Date(this.paymentRequest.payment.createdAt);

    return this.paymentRequest.payment.createdAt
      ? new Date(initialDate.setHours(initialDate.getHours() + 24))
      : new Date(new Date().setHours(new Date().getHours() + 24));
  }

  async handleLoadPaymentRequest(paymentRequest: any) {
    this.paymentRequest = paymentRequest.record;
    if (this.paymentRequest?.payer.whoIsMakingThePayment) {
      this.payer.patchValue({
        whoIsMakingThePayment: this.paymentRequest.payer.whoIsMakingThePayment,
      });
      this.cdr.detectChanges();
    }

    this.paymentAmount = this.paymentRequest.payment.amount;
    this.paymentCurrency = this.paymentRequest.payment.currency;
    this.companyName = paymentRequest.record.company.name;
    this.companyLogo = paymentRequest.record.company.logo;
    this.uploadedDocumentsId = {
      invoice: this.paymentRequest.payment.invoice,
      nationalId: this.paymentRequest.payment.nationalId,
      proofOfPayment: this.paymentRequest.payment.proofOfPayment,
    };

    this.intentExpirationDate = this.instructionExpiry.toLocaleDateString(
      'en-US',
      {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      },
    );

    this.TRANSLATE.use(this.paymentRequest.language);

    if (
      (this.status && this.successStatuses.includes(this.status)) ||
      this.successStatuses.includes(this.paymentRequest.status)
    ) {
      this.status = 'Successful';
      if (
        this.paymentRequest.successRedirectUrl &&
        this.PRMS.params.get('tm_payment_status')
      ) {
        this.document.location.href = this.paymentRequest.successRedirectUrl;
      }

      this.tracking = this.mapPaymentRequestTracking();
      return;
    }

    if (this.paymentRequest?.payment) {
      this.payment.patchValue(this.paymentRequest.payment, {
        emitEvent: false,
        onlySelf: true,
      });
    }

    if (this.paymentRequest?.student) {
      this.student.patchValue(this.paymentRequest.student, {
        emitEvent: false,
        onlySelf: true,
      });

      this.payment.patchValue({
        countryPayFrom: this.paymentRequest.student.country,
      });
    }

    if (this.paymentRequest?.type === 'AGENCY_PAYMENT') {
      this.payer.get('whoIsMakingThePayment')?.setValue('OTHER');
    }

    this.onPayerCountryChange();

    const now = new Date();
    const paymentRequestCreatedAt = paymentRequest?.record?.payment?.createdAt;
    const createdInThreeDays = moment(paymentRequestCreatedAt).add(3, 'days');

    this.redirectUrl = this.DS.bypassSecurityTrustResourceUrl(
      this.paymentRequest.payment.paymentIntentLinkUrl,
    );

    if (
      paymentRequestCreatedAt &&
      createdInThreeDays.isAfter(moment()) &&
      paymentRequest?.record?.payment?.countryPayFrom === 'mx' &&
      !!paymentRequest?.record?.payment?.paymentIntentLinkUrl
    ) {
      this.moveToLastStep(this.payment.value.paymentMethod);
    } else {
      if (this.instructionExpiry > now) {
        this.moveToLastStep(this.payment.value.paymentMethod);
      }

      if (this.instructionExpiry < now) {
        this.expired = true;
        this.moveToLastStep(this.payment.value.paymentMethod);
      }
    }

    const intents = this.paymentRequest?.payment_intents;
    if (Array.isArray(intents) && intents.length > 0) {
      const status = intents[0]?.status;
      if (status === 'CANCELED' || status === 'EXPIRED') {
        this.status = 'Cancelled';
        this.moveToLastStep(this.payment.value.paymentMethod);
        return;
      }
    }
  }

  getInstructionsExpiryDate(): Date {
    return this.instructionExpiry;
  }

  handleWhoIsMakingThePaymentChanges(): void {
    const { whoIsMakingThePayment } = this.payer.value;
    const prefillPayerValues =
      !whoIsMakingThePayment ||
      this.paymentRequest.payer.whoIsMakingThePayment === whoIsMakingThePayment;

    if (whoIsMakingThePayment === 'STUDENT') {
      this.hideDetails = true;
      this.payer.patchValue(this.student.value);
      this.getDocumentValidations();
      this.disableStudentFields();

      if (this.paymentRequest.student.passport) {
        this.documentIdToUpload = [
          {
            label: 'uploadDocuments:passportOrId',
            key: 'nationalId',
            status: UPLOAD_STATUS.Uploaded,
          },
        ];
        this.uploadedDocumentsIds.nationalId =
          this.paymentRequest.student.passport;
        this.wasPassportAlreadyUploaded = true;
        this.handleIdFileUpload();
      }
      return;
    } else {
      this.hideDetails = false;
      this.payer.reset();
      this.payer.patchValue({ payer: 'Agency' });
      this.payer.patchValue({
        whoIsMakingThePayment: whoIsMakingThePayment,
      });
      this.payer.patchValue({ country: this.payment.value.countryPayFrom });
      this.enableStudentFields();
    }

    if (prefillPayerValues) {
      this.payer.patchValue(this.paymentRequest.payer, {
        emitEvent: false,
        onlySelf: true,
      });

      if (this.paymentRequest.payer.passport) {
        this.documentIdToUpload = [
          {
            label: 'uploadDocuments:passportOrId',
            key: 'nationalId',
            status: UPLOAD_STATUS.Uploaded,
          },
        ];
        this.uploadedDocumentsIds.nationalId =
          this.paymentRequest.payer.passport;
        this.wasPassportAlreadyUploaded = true;
        this.handleIdFileUpload();
      }
    }
  }

  onStepChange({ selectedIndex: currentIndex }: StepperSelectionEvent) {
    const payerInformationStep = 2;
    if (currentIndex == payerInformationStep) {
      this.handleWhoIsMakingThePaymentChanges();
    }

    this.updateStepHeaders(currentIndex);
  }

  /**
   * Updates the visibility of mat-stepper headers based on the current step index and screen mode.
   * Description:
   * - In desktop mode (`!this.isMobile`): All headers are displayed (`display: 'flex'`).
   * - In mobile mode (`this.isMobile`): Only the current step and the next step are displayed (`display: 'grid'`), others are hidden (`display: 'none'`).
   * @param currentIndex - The index of the currently selected step.
   * @author Martin Batun Tec.
   */
  updateStepHeaders(currentIndex: number): void {
    const stepHeaders = document.querySelectorAll('.mat-step-header');
    stepHeaders.forEach((header: Element, index: number) => {
      const headerElement = header as HTMLElement;
      if (!this.isMobile) {
        headerElement.style.display = 'flex';
      } else if (index === currentIndex || index === currentIndex + 1) {
        headerElement.style.display = 'grid';
      } else {
        headerElement.style.display = 'none';
      }
    });
  }

  private disableStudentFields() {
    [
      'firstname',
      'lastname',
      'country',
      'city',
      'address',
      'postalCode',
      'email',
      'phoneNumber',
    ].forEach((controlName) => {
      this.payer
        .get(controlName)
        ?.disable({ onlySelf: true, emitEvent: false });
    });
  }

  private enableStudentFields() {
    [
      'firstname',
      'lastname',
      'country',
      'city',
      'address',
      'postalCode',
      'email',
      'phoneNumber',
    ].forEach((controlName) => {
      this.payer.get(controlName)?.enable();
    });
  }

  isTheFormOnIframe(): boolean {
    return this.paymentRequest?.type === 'AGENCY_PAYMENT';
  }

  openTermsAndConditionsDialog(): void {
    this.HTTP.get(
      `assets/i18n/terms-and-conditions/${this.TRANSLATE.currentLang}.txt`,
      { responseType: 'text' },
    ).subscribe({
      next: (data) => this.openDialog('shared:terms&conditions', data),
      error: () => {
        //Defaults to english in case of an error
        this.HTTP.get(`assets/i18n/terms-and-conditions/en.txt`, {
          responseType: 'text',
        }).subscribe({
          next: (data) => this.openDialog('shared:termsOfUse', data),
        });
      },
    });
  }

  openDialog(title: string, data: string) {
    this.MD.open(TermsAndConditionsComponent, {
      width: '95vw',
      height: '95vh',
      panelClass: 'sm-full',
      data: {
        title: title,
        data: data,
        inject: true,
      },
    });
  }

  /**
   * Displays an alert or a dialog before changing the payment method.
   * @author Martin Batun Tec.
   */
  changePaymentMethodAlert(): void {
    this.MD.open(this.dialogTemplate, {
      width: '350px',
    })
      .beforeClosed()
      .subscribe((result) => {
        if (result) {
          this.changePaymentMethod();
        }
      });
  }

  changePaymentMethod() {
    this.stepper.linear = false;
    this.paymentRequest.payment.instructions = null;
    this.paymentRequest.payment.convertedAmount = null;
    this.paymentRequest.payment.convertedCurrency = null;
    this.payment.patchValue({ paymentMethod: null });
    this.showBankTransfer = false;
    this.showCreditCard = false;
    this.PRMS.clean();
    setTimeout(() => {
      this.stepper.selectedIndex = 0;
      this.stepper.linear = true;
    }, 100);
  }

  private updateHasUploadedDocs() {
    const filesCount = this.countFilesUploaded();
    this.hasUploadedDocs =
      filesCount > 0
        ? filesCount < this.proofOfPaymentToUpload.length
          ? UploadedDocsStatus.Partial
          : UploadedDocsStatus.Yes
        : UploadedDocsStatus.No;
  }

  private countFilesUploaded() {
    let total = 0;

    const keys = Object.keys(this.uploadedDocumentsId) as [keyof EntryKeys];

    for (const key of keys) {
      if (this.uploadedDocumentsId[key]) {
        total += 1;
      }
    }

    return total;
  }

  private isReadyToEmit() {
    if (this.hasUploadedDocs === UploadedDocsStatus.Yes) {
      return true;
    }

    return false;
  }

  private getUploadedDocuments() {
    return {
      ...this.uploadedDocumentsId,
      pushToEmail: this.isReadyToEmit(),
    };
  }

  private mapPaymentRequestTracking(): Tracking {
    const {
      payer,
      student,
      payment,
      order,
      uuid,
      payment_intents,
      successRedirectUrl,
    } = this.paymentRequest;

    return {
      view: 1,
      id: uuid,
      hi: `${payer.firstname ?? student.firstname ?? ''} ${
        payer.lastname ?? student.lastname ?? ''
      }`,
      infoPrice: {
        primary: {
          formatCurrency: {
            amount: payment.amount,
            currency: payment.currency,
          },
        },
        secondary: {
          basic: payment.payableAmount,
        },
      },
      payout: {
        date: payment.derivedPaymentDate || payment.createdAt,
        paymentMethod: payment.paymentMethod,
        requested: payment.payableAmount
          ? {
              currency: payment.payableAmount,
              amount: payment.payableAmount,
            }
          : undefined,
        received: {
          currency: payment.currency,
          amount: payment.amount,
        },
        receiptDocumentId: payment.receiptDocumentId,
      },
      order,
      student: {
        name: `${student.firstname} ${student.lastname}`,
        country: student.country,
        city: student.city,
        email: student.email,
      },
      showBtnDownload: !!payment_intents[0].receipt_document_id,
      successRedirectUrl: successRedirectUrl,
    };
  }

  uploadDocuments() {
    this.updateHasUploadedDocs();

    console.log('uploadDocuments');

    const documents = this.getUploadedDocuments();

    this.API.patch(
      `v1/public/payment-requests/${this.paymentRequest.uuid}/payment-intents/`,
      documents,
    ).subscribe((response: any) => {
      if (response && response.proofOfPayment) {
        this.uploadedDocumentsId.proofOfPayment = response.proofOfPayment;
        const proofOfPaymentDoc = this.proofOfPaymentToUpload.find(
          ({ key }) => key === 'proofOfPayment',
        );
        if (proofOfPaymentDoc) {
          proofOfPaymentDoc.status = UPLOAD_STATUS.Uploaded;
        }
        this.updateHasUploadedDocs();
        this.cdr.detectChanges();
      }

      if (documents.pushToEmail) {
        this.TRANSLATE.get(
          'paymentRequest:onlyProofUploadedSubtitle',
        ).subscribe((value) => {
          this.ALERT.success(value);
        });
      }
    });
  }

  handleIdFileUpload(): void {
    const nationalId = this.uploadedDocumentsIds.nationalId;
    this.wasPassportAlreadyUploaded =
      this.wasPassportAlreadyUploaded && nationalId != null;
    this.payer.patchValue({ nationalId });
    this.updateHasUploadedDocs();
    this.cdr.detectChanges();
  }

  /**
   * Checks if changing the payment method is allowed.
   * @returns {boolean} True if the payment method can be changed, otherwise false.
   * @author Martin Batun Tec.
   */
  isChangingPaymentAllowed(): boolean {
    const version = this.paymentRequest?.paymentRequestVersion ?? 1;
    const isCardPayment = CARD_PAYMENT_METHODS.includes(
      this.payment.value.paymentMethod,
    );
    const isNotCancelled = this.status !== 'Cancelled';
    const noProofOfPayment =
      version === 1
        ? this.hasUploadedDocs === 'NO'
        : !this.getUploadedDocuments().proofOfPayment;

    return (
      isNotCancelled && (isCardPayment || (!this.expired && noProofOfPayment))
    );
  }

  private async changeRequestIdFeatureFlag() {
    this.requestIdUpload = await this.CYCLE.getVariable(
      'request-payer-doc-id-upload',
      false,
    );
  }

  private setUploadDocumentAsRequired(): void {
    if (this.documentPassportRequired && this.requestIdUpload) {
      this.payer.get('nationalId')!.setValidators([Validators.required]);
    } else {
      this.payer.get('nationalId')!.clearValidators();
      this.payer.get('nationalId')!.setValidators([]);
    }
    this.payer.get('nationalId')!.updateValueAndValidity();
  }

  isBankTransfer() {
    return BANK_TRANSFER_METHODS.includes(this.payment.value.paymentMethod);
  }
}
